import React, { useState, useEffect, useCallback } from 'react';
import { Truck, RefreshCw, Download, XCircle, Send, AlertCircle, CheckCircle, Clock, Package } from 'lucide-react';
import { shipmentService, SHIPMENT_STATUS_LABEL } from '../../api/shipmentService';
import SenditColisModal from './SenditColisModal';

function StatusPill({ status }) {
  const cfg = SHIPMENT_STATUS_LABEL[status] || { label: status, color: '#444', bg: '#eee' };
  return (
    <span style={{
      fontSize: 12, fontWeight: 600, padding: '4px 10px', borderRadius: 99,
      background: cfg.bg, color: cfg.color, whiteSpace: 'nowrap',
    }}>
      {cfg.label}
    </span>
  );
}

function fmtDate(d) {
  if (!d) return '';
  try { return new Date(d).toLocaleString('fr-FR'); } catch { return d; }
}

export default function ShipmentPanel({ order, onShipmentChange }) {
  const [shipment, setShipment] = useState(order?.shipment || null);
  const [loading, setLoading] = useState(false);
  const [action, setAction] = useState(null); // 'create' | 'refresh' | 'label' | 'cancel'
  const [error, setError] = useState('');
  const [info, setInfo] = useState('');
  const [showModal, setShowModal] = useState(false);

  const fetchShipment = useCallback(async () => {
    if (!order?.shipment?.id) {
      setShipment(null);
      return;
    }
    try {
      setLoading(true);
      const data = await shipmentService.show(order.shipment.id);
      setShipment(data);
    } catch (e) {
      // fall through
    } finally {
      setLoading(false);
    }
  }, [order?.shipment?.id]);

  useEffect(() => {
    setShipment(order?.shipment || null);
  }, [order?.shipment]);

  // Auto-refresh every 30s while the modal is open and the shipment is non-terminal.
  // Silent: no spinner, no toast, just background updates so the admin sees status
  // changes without clicking. Stops automatically once the shipment reaches a final state.
  useEffect(() => {
    if (!shipment?.id) return;
    const terminal = ['delivered', 'returned', 'cancelled', 'failed'];
    if (terminal.includes(shipment.status)) return;

    const intervalId = setInterval(async () => {
      try {
        const data = await shipmentService.show(shipment.id);
        setShipment(data);
      } catch {
        // ignore — next tick will retry
      }
    }, 30_000);
    return () => clearInterval(intervalId);
  }, [shipment?.id, shipment?.status]);

  const clearMessages = () => { setError(''); setInfo(''); };

  const handleCreated = (newShipment) => {
    setShipment(newShipment);
    setInfo('Colis créé chez Sendit.');
    onShipmentChange?.(newShipment);
  };

  const handleRefresh = async () => {
    clearMessages();
    setAction('refresh');
    try {
      const resp = await shipmentService.refresh(shipment.id);
      setShipment(resp.shipment);
      setInfo(resp.changed ? 'Statut mis à jour.' : 'Aucun changement de statut.');
      onShipmentChange?.(resp.shipment);
    } catch (e) {
      setError(e.response?.data?.message || 'Échec du rafraîchissement.');
    } finally {
      setAction(null);
    }
  };

  const handleLabel = async () => {
    clearMessages();
    setAction('label');
    try {
      const resp = await shipmentService.label(shipment.id);
      if (resp.label_url) {
        window.open(resp.label_url, '_blank', 'noopener');
        setShipment(resp.shipment);
      } else {
        setError('Aucune étiquette disponible.');
      }
    } catch (e) {
      setError(e.response?.data?.message || "Échec de récupération de l'étiquette.");
    } finally {
      setAction(null);
    }
  };

  const handleCancel = async () => {
    if (!window.confirm('Supprimer ce colis chez Sendit ? Cette action est irréversible.')) return;
    clearMessages();
    setAction('cancel');
    try {
      const resp = await shipmentService.cancel(shipment.id);
      setShipment(resp.shipment);
      setInfo(resp.message || 'Colis supprimé.');
      onShipmentChange?.(resp.shipment);
    } catch (e) {
      setError(e.response?.data?.message || 'Échec de la suppression.');
    } finally {
      setAction(null);
    }
  };

  // ─── Render ──────────────────────────────────────────────────────────────

  // No shipment yet (or previous one failed/cancelled) → show "create" CTA + modal
  if (!shipment || shipment.status === 'failed' || shipment.status === 'cancelled') {
    return (
      <>
        <div className="details-card" style={{ marginBottom: 24 }}>
          <h3><Truck size={18} />Livraison Sendit</h3>

          {error && (
            <div className="inline-alert error" style={{ marginBottom: 12 }}>
              <AlertCircle size={16} /> {error}
            </div>
          )}
          {info && (
            <div className="inline-alert success" style={{ marginBottom: 12 }}>
              <CheckCircle size={16} /> {info}
            </div>
          )}

          {shipment && (
            <div style={{ marginBottom: 12, fontSize: 13, color: '#666' }}>
              Dernière tentative&nbsp;: <StatusPill status={shipment.status} />
              {' · '}{fmtDate(shipment.updated_at)}
              {shipment.last_response?.message && (
                <div style={{ marginTop: 6, color: '#842029' }}>{shipment.last_response.message}</div>
              )}
            </div>
          )}

          <button
            type="button"
            className="btn btn-primary"
            onClick={() => setShowModal(true)}
          >
            <Send size={16} /> Créer un colis Sendit
          </button>
        </div>

        {showModal && (
          <SenditColisModal
            orderId={order.id}
            onClose={() => setShowModal(false)}
            onCreated={handleCreated}
          />
        )}
      </>
    );
  }

  // Shipment exists → tracking view
  return (
    <div className="details-card" style={{ marginBottom: 24 }}>
      <h3><Truck size={18} />Livraison Sendit</h3>

      {error && (
        <div className="inline-alert error" style={{ marginBottom: 12 }}>
          <AlertCircle size={16} /> {error}
        </div>
      )}
      {info && (
        <div className="inline-alert success" style={{ marginBottom: 12 }}>
          <CheckCircle size={16} /> {info}
        </div>
      )}

      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 24, alignItems: 'flex-start', marginBottom: 16 }}>
        <div>
          <div style={{ fontSize: 12, color: '#666' }}>Statut</div>
          <div style={{ marginTop: 4 }}><StatusPill status={shipment.status} /></div>
        </div>
        <div>
          <div style={{ fontSize: 12, color: '#666' }}>Code de suivi</div>
          <div style={{ fontWeight: 600, fontFamily: 'monospace', marginTop: 4 }}>
            {shipment.provider_code || '—'}
          </div>
        </div>
        <div>
          <div style={{ fontSize: 12, color: '#666' }}>Montant COD</div>
          <div style={{ fontWeight: 600, marginTop: 4 }}>
            {parseFloat(shipment.cod_amount || 0).toFixed(2)} DH
          </div>
        </div>
        <div>
          <div style={{ fontSize: 12, color: '#666' }}>Dernier sync</div>
          <div style={{ marginTop: 4, fontSize: 13 }}>{fmtDate(shipment.last_synced_at)}</div>
        </div>
      </div>

      {/* What was actually sent to Sendit */}
      {shipment.request_payload && (
        <details style={{
          marginBottom: 16, background: '#f8f9fa', border: '1px solid #e9ecef',
          borderRadius: 6, padding: '8px 12px', fontSize: 13,
        }}>
          <summary style={{ cursor: 'pointer', fontWeight: 600, color: '#444', display: 'inline-flex', alignItems: 'center', gap: 6 }}>
            <Package size={14} /> Détails du colis envoyé
          </summary>
          <div style={{ display: 'grid', gridTemplateColumns: 'auto 1fr', gap: '6px 12px', marginTop: 10, color: '#555' }}>
            {[
              ['Destinataire', shipment.request_payload.name],
              ['Téléphone',    shipment.request_payload.phone],
              ['Adresse',      shipment.request_payload.address],
              ['Ville',        shipment.request_payload.city],
              ['Référence',    shipment.request_payload.reference],
              ['Description',  shipment.request_payload.comment],
              ['Poids',        shipment.request_payload.weight ? `${shipment.request_payload.weight} kg` : null],
              ['Ouverture autorisée', typeof shipment.request_payload.allow_open === 'boolean'
                ? (shipment.request_payload.allow_open ? 'Oui' : 'Non') : null],
            ].filter(([_, v]) => v !== null && v !== undefined && v !== '').map(([k, v]) => (
              <React.Fragment key={k}>
                <div style={{ fontWeight: 600, color: '#666', whiteSpace: 'nowrap' }}>{k}</div>
                <div style={{ wordBreak: 'break-word' }}>{v}</div>
              </React.Fragment>
            ))}
          </div>
        </details>
      )}

      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 20 }}>
        <button
          type="button"
          className="btn btn-secondary"
          onClick={handleRefresh}
          disabled={!!action || loading}
        >
          <RefreshCw size={16} className={action === 'refresh' ? 'spin' : ''} />
          {action === 'refresh' ? 'Synchronisation…' : 'Rafraîchir le statut'}
        </button>
        <button
          type="button"
          className="btn btn-secondary"
          onClick={handleLabel}
          disabled={!!action}
        >
          <Download size={16} />
          {action === 'label' ? '...' : "Étiquette PDF"}
        </button>
        {!['delivered', 'returned', 'cancelled'].includes(shipment.status) && (
          <button
            type="button"
            className="btn btn-danger"
            onClick={handleCancel}
            disabled={!!action}
          >
            <XCircle size={16} />
            {action === 'cancel' ? 'Suppression…' : 'Supprimer le colis'}
          </button>
        )}
      </div>

      {/* Timeline */}
      {shipment.events && shipment.events.length > 0 && (
        <div>
          <h4 style={{ fontSize: 14, marginBottom: 12, color: '#444' }}>Historique</h4>
          <ol style={{ listStyle: 'none', padding: 0, margin: 0, borderLeft: '2px solid #e9ecef' }}>
            {shipment.events.map((ev) => (
              <li key={ev.id} style={{ paddingLeft: 16, paddingBottom: 14, position: 'relative' }}>
                <span style={{
                  position: 'absolute', left: -7, top: 4,
                  width: 12, height: 12, borderRadius: '50%',
                  background: SHIPMENT_STATUS_LABEL[ev.status]?.color || '#888',
                  border: '2px solid white', boxShadow: '0 0 0 1px #ccc',
                }} />
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                  <StatusPill status={ev.status} />
                  <span style={{ fontSize: 12, color: '#666' }}>
                    <Clock size={11} style={{ verticalAlign: 'middle' }} />{' '}
                    {fmtDate(ev.occurred_at)}
                  </span>
                  {ev.source && (
                    <span style={{ fontSize: 11, color: '#999' }}>· {ev.source}</span>
                  )}
                </div>
                {ev.note && (
                  <div style={{ fontSize: 13, color: '#444', marginTop: 4 }}>{ev.note}</div>
                )}
                {ev.raw_status && (
                  <div style={{ fontSize: 11, color: '#999', marginTop: 2, fontFamily: 'monospace' }}>
                    raw: {ev.raw_status}
                  </div>
                )}
              </li>
            ))}
          </ol>
        </div>
      )}

      <style>{`
        @keyframes _spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
        .spin { animation: _spin 1s linear infinite; }
      `}</style>
    </div>
  );
}
